import React from 'react';
import { AlertTriangle, Plus, RotateCcw } from 'lucide-react';
import { C } from '../../theme.js';
import { Code, FieldLabel, Select, TextInput } from '../../components/Primitives.jsx';
import { SOURCE_COLUMNS } from '../../data.js';
import { ColumnsContext } from '../StepInspector.jsx';

// ── Output column picker (segmented: pick existing column or name a new one) ──
export function OutputColumnField({ value, onChange, placeholder = 'new_column_name', hint, columns }) {
  const ctxCols = React.useContext(ColumnsContext);
  const cols = columns || ctxCols || SOURCE_COLUMNS;
  // Distinguish "overwrite an existing column" vs "create a new one". Mode
  // is purely a user choice (the segmented buttons below) — it used to also
  // auto-snap to "existing" the instant `value` matched a column name while
  // the user was still typing (e.g. typing "From_rate" with "From" already
  // a real column: the moment the text read exactly "From", this field
  // silently swapped to a dropdown, cutting the rest of the name off
  // mid-keystroke). Now typing is never interrupted; a same-named collision
  // is flagged as a warning instead, in whichever mode the user is in.
  const matchesExisting = cols.some(c => c.name === value);
  const [mode, setMode] = React.useState(matchesExisting ? 'existing' : 'new');

  return (
    <div>
      <FieldLabel hint={hint || 'Where the result is written'}>Output column</FieldLabel>
      <div style={{
        display: 'flex', gap: 2, padding: 3,
        background: C.input, border: `1px solid ${C.borderStrong}`,
        borderRadius: 8, marginBottom: 8,
      }}>
        {[
          { id: 'new', label: 'New column', icon: Plus },
          { id: 'existing', label: 'Overwrite existing', icon: RotateCcw },
        ].map(opt => {
          const on = mode === opt.id;
          const OptIcon = opt.icon;
          return (
            <button
              key={opt.id}
              onClick={() => {
                setMode(opt.id);
                // Reset the value so the new control isn't seeded with a stale entry.
                if (opt.id === 'new' && matchesExisting) onChange('');
                if (opt.id === 'existing' && !matchesExisting) onChange(cols[0]?.name || '');
              }}
              style={{
                flex: 1, padding: '6px 10px',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                background: on ? 'var(--info-bg)' : 'transparent',
                color: on ? C.accentBright : C.fg4,
                border: 'none', borderRadius: 5, cursor: 'pointer',
                fontFamily: 'inherit', fontSize: '.78rem', fontWeight: 600,
              }}
            ><OptIcon size={13} />{opt.label}</button>
          );
        })}
      </div>
      {mode === 'existing' ? (
        <Select
          value={value}
          onChange={onChange}
          options={cols.map(co => ({
            value: co.name,
            label: `${co.name}  ·  ${co.type}`,
          }))}
          style={{ width: '100%' }}
        />
      ) : (
        <TextInput mono value={value || ''} onChange={onChange} placeholder={placeholder} />
      )}
      {mode === 'existing' && value && (
        <div style={{
          color: C.warn, fontSize: '.7rem', marginTop: 6,
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <span style={{ display: 'inline-flex' }}><AlertTriangle size={12} /></span>
          <span>Existing values in <Code>{value}</Code> will be overwritten.</span>
        </div>
      )}
      {mode === 'new' && value && matchesExisting && (
        <div style={{
          color: C.warn, fontSize: '.7rem', marginTop: 6,
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <span style={{ display: 'inline-flex' }}><AlertTriangle size={12} /></span>
          <span>A column named <Code>{value}</Code> already exists — this will overwrite it, not create a new column.</span>
        </div>
      )}
    </div>
  );
}
